import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { addToCart, fetchCart } from '../../features/cart/cartSlice';
import { useCartTransition } from '../../hooks/useViewTransition';
import { useStoreBranding } from '../../hooks/useStoreBranding';
import StorefrontHero from '../../components/storefront/StorefrontHero';
import StorefrontCampaignHero from '../../components/storefront/StorefrontCampaignHero';
import FeaturedProductsRow from '../../components/storefront/FeaturedProductsRow';
import ShopByCategory from '../../components/storefront/ShopByCategory';
import ReviewsCarousel from '../../components/storefront/ReviewsCarousel';
import HomeProductCatalog from '../../components/storefront/HomeProductCatalog';
import { ToastProvider, useToast } from '../../components/ui/Toast';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import { AdPlacement, AD_PLACEMENTS } from '../../components/ads';
import { useGA4 } from '../../contexts/GA4Context';
import axios from '../../axiosConfig';

// Helper to check if we're on a subdomain
const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

// SEO meta tag helper
const setMetaTag = (property, content) => {
  if (!content) return;
  const attr = property.startsWith('og:') ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, property);
    document.head.appendChild(tag);
  }
  tag.content = content;
};

const HomePageContent = () => {
  const { store, theme, themeStyles } = useOutletContext() || {};
  const dispatch = useDispatch();
  const { transitionCartAction, isUpdating } = useCartTransition();
  const toast = useToast();
  const navigate = useNavigate();
  const { trackViewItemList, trackAddToCart, trackSearch } = useGA4();
  const hasTrackedProducts = useRef(false);

  // Get currency formatting from storefront context
  const { formatPrice: contextFormatPrice } = useStorefrontCurrency();

  // Check if we're on a subdomain
  const onSubdomain = isOnSubdomain();

  // Update browser tab title and favicon based on store branding
  useStoreBranding(store);

  // SEO Meta Tags
  useEffect(() => {
    if (!store) return;
    const description = store.heroDescription || store.description || `Shop at ${store.name}`;
    setMetaTag('description', description);
    setMetaTag('og:title', store.name || 'Store');
    setMetaTag('og:description', description);
    setMetaTag('og:image', store.logo || '');
    setMetaTag('og:url', window.location.href);
  }, [store]);

  // Hero-flush handling moved to the outer HomePage wrapper so it runs
  // in both code paths (legacy HomePageContent and premium ThemedHome).

  // Helper to generate the correct path based on subdomain vs path-based routing
  const getStorePath = (path = '') => {
    if (onSubdomain) {
      // On subdomain: use root-level paths
      return path ? `/${path}` : '/';
    } else {
      // Path-based: use /s/:slug/ format
      return path ? `/s/${store?.slug}/${path}` : `/s/${store?.slug}`;
    }
  };
  const products = useSelector(state => state.store?.products || []);
  const campaigns = useSelector(state => state.store?.campaigns || []);
  const activeHeroCampaign = campaigns.find(campaign => campaign?.heroEnabled);

  // State for filters and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;

  // Refs for smooth-scroll targets used by the hero's CTAs and
  // the "Shop by Category" view-all action.
  const productsSectionRef = useRef(null);
  const categoriesSectionRef = useRef(null);

  // Wishlist state
  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // Optional homepage sections are merchant-controlled and hidden by default.
  const storefrontDisplay = store?.settings?.storefrontDisplay || {};
  const SHOW_CATEGORIES = storefrontDisplay.showCategories === true;
  const SHOW_BEST_SELLERS = storefrontDisplay.showBestSellers === true;
  const SHOW_TRENDING = storefrontDisplay.showTrending === true;
  const SHOW_NEW_ARRIVALS = storefrontDisplay.showNewArrivals === true;
  const SHOW_REVIEWS = storefrontDisplay.showReviews === true;
  const MAX_CATEGORIES_DISPLAY = storefrontDisplay.categoriesMax || 8;
  const BEST_SELLERS_MAX = storefrontDisplay.bestSellersMax || 4;
  const TRENDING_MAX = storefrontDisplay.trendingMax || 4;
  const NEW_ARRIVALS_MAX = storefrontDisplay.newArrivalsMax || 4;

  // Capture discount code from URL and store it
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const discountCode = urlParams.get('discount');

    if (discountCode && store?.slug) {
      // Store discount code in sessionStorage for this store
      const storageKey = `pendingDiscount_${store.slug}`;
      sessionStorage.setItem(storageKey, discountCode);
      console.log(`[HomePage] Discount code ${discountCode} saved for checkout`);

      // Show toast notification
      toast.success(`Discount code ${discountCode} will be applied at checkout!`, {
        duration: 5000
      });

      // Clean URL by removing discount parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('discount');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [store?.slug, toast]);

  // Fetch cart and wishlist on mount
  React.useEffect(() => {
    // Fetch cart from backend session for all users (guest and authenticated)
    if (store?.slug) {
      console.log('HomePage: Fetching cart from backend session for store:', store.slug);
      dispatch(fetchCart(null)); // null for guest users
    }

    // Fetch wishlist (only if customer is logged in)
    const fetchWishlist = async () => {
      try {
        const response = await fetch('/api/customer/wishlist', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setWishlist(data.wishlist || []);
        }
      } catch (error) {
        // Customer not logged in or error - silently ignore
        console.log('Wishlist fetch skipped:', error.message);
      }
    };

    fetchWishlist();
  }, [dispatch, store?.slug]);

  // Toggle product in wishlist
  const toggleWishlist = async (productId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setWishlistLoading(true);

    try {
      const response = await fetch('/api/customer/wishlist/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId })
      });

      if (response.ok) {
        const data = await response.json();
        // Update local wishlist state
        if (data.inWishlist) {
          setWishlist(prev => [...prev, { id: productId }]);
          toast.success('Added to wishlist! View it in your customer dashboard.');
        } else {
          setWishlist(prev => prev.filter(item => item.id !== productId));
          toast.info('Removed from wishlist');
        }
      } else if (response.status === 401) {
        // Store product ID to add after login
        sessionStorage.setItem('pendingWishlistProduct', productId);

        toast.warning('Redirecting to login to save your wishlist...');

        // Redirect to customer login after 1.5s
        setTimeout(() => {
          const currentUrl = window.location.href;
          const hostname = window.location.hostname;

          // Check if on subdomain (e.g., storename.revstarng.com)
          const isOnSubdomain = hostname.includes('.') && !hostname.startsWith('www.');

          let loginUrl;
          if (isOnSubdomain) {
            // On subdomain - use same subdomain for customer login
            loginUrl = `${window.location.protocol}//${hostname}/login?redirect=${encodeURIComponent(currentUrl)}&wishlist=${productId}`;
          } else {
            // Path-based routing - use store slug
            loginUrl = `/s/${store?.slug}/login?redirect=${encodeURIComponent(currentUrl)}&wishlist=${productId}`;
          }

          window.location.href = loginUrl;
        }, 1500);
      } else {
        toast.error('Failed to update wishlist. Please try again.');
      }
    } catch (error) {
      console.error('Wishlist toggle error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setWishlistLoading(false);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.some(item => item.id === productId);
  };

  // Filter active products
  const activeProducts = Array.isArray(products) ? products.filter(p => p.status === 'ACTIVE') : [];

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(activeProducts.map(p => p.category).filter(Boolean))];
    return cats;
  }, [activeProducts]);

  // Get Best Sellers products
  const bestSellers = useMemo(() => {
    return activeProducts.filter(p => p.bestSeller === true || p.isBestSeller === true);
  }, [activeProducts]);

  // Get Trending products
  const trendingProducts = useMemo(() => {
    return activeProducts.filter(p => p.trending === true || p.isTrending === true);
  }, [activeProducts]);

  // Get New Arrivals (products marked as new OR created within last 30 days)
  const newArrivals = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return activeProducts.filter(p => {
      // Check if product has newArrival flag
      if (p.newArrival === true || p.isNewArrival === true) {
        return true;
      }
      // Check if product was created recently
      if (p.createdAt) {
        const createdDate = new Date(p.createdAt);
        return createdDate >= thirtyDaysAgo;
      }
      return false;
    });
  }, [activeProducts]);

  // Filter and search products
  const filteredProducts = useMemo(() => {
    let filtered = [...activeProducts];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Sort
    if (sortBy === 'a-z') {
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'z-a') {
      filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
    }

    return filtered;
  }, [activeProducts, searchQuery, selectedCategory, sortBy]);

  // GA4: Track product list views
  useEffect(() => {
    if (hasTrackedProducts.current || !activeProducts.length) return;
    hasTrackedProducts.current = true;
    trackViewItemList(activeProducts.slice(0, 20), 'Homepage Products');
    if (SHOW_BEST_SELLERS && bestSellers.length > 0) trackViewItemList(bestSellers.slice(0, 10), 'Best Sellers');
    if (SHOW_TRENDING && trendingProducts.length > 0) trackViewItemList(trendingProducts.slice(0, 10), 'Trending');
    if (SHOW_NEW_ARRIVALS && newArrivals.length > 0) trackViewItemList(newArrivals.slice(0, 10), 'New Arrivals');
  }, [activeProducts, bestSellers, trendingProducts, newArrivals, SHOW_BEST_SELLERS, SHOW_TRENDING, SHOW_NEW_ARRIVALS, trackViewItemList]);

  // GA4: Track search
  useEffect(() => {
    if (searchQuery.trim().length >= 3) {
      const timer = setTimeout(() => trackSearch(searchQuery.trim()), 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, trackSearch]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, sortBy]);

  // Format price helper - uses currency context for proper symbol and conversion
  const formatPrice = (price) => {
    return contextFormatPrice(price);
  };

  // Whether the merchant has uploaded a hero image. When they have, the
  // hero gets a dark image overlay so white text + drop shadow read well.
  // When they haven't, the hero is a flat themed surface (light or dark
  // mode) and text shadows would just look like grime.
  return (
    <div style={{
      backgroundColor: themeStyles?.background || '#ffffff',
      minHeight: '100vh'
    }}>
      {/* Integrated hero card: merchant photo as backdrop, brand
          mark + headline + tagline + two CTAs overlaid, trust
          strip beneath. See StorefrontHero.js. */}
      {activeHeroCampaign ? (
        <StorefrontCampaignHero
          campaign={activeHeroCampaign}
          store={store}
          onCtaClick={() => {
            productsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      ) : (
        <StorefrontHero
          store={store}
          themeStyles={themeStyles}
          onShopClick={() => {
            productsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          onBrowseClick={() => {
            (categoriesSectionRef.current || productsSectionRef.current)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      )}

      {/* Homepage Hero Ad - Text ad below hero */}
      {store?.slug && (
        <AdPlacement
          storeSlug={store.slug}
          placement={AD_PLACEMENTS.HOMEPAGE_HERO}
          showAdvertiseBlurb={false}
        />
      )}

      {/* Homepage Banner Ad */}
      {store?.slug && (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <AdPlacement
            storeSlug={store.slug}
            placement={AD_PLACEMENTS.HOMEPAGE_BANNER}
            showAdvertiseBlurb={false}
          />
        </div>
      )}

      {SHOW_CATEGORIES && categories.length > 0 && (
        <div ref={categoriesSectionRef} style={{ scrollMarginTop: '80px' }}>
          <ShopByCategory
            categories={categories}
            activeProducts={activeProducts}
            selectedCategory={selectedCategory}
            maxBeforeCollapse={MAX_CATEGORIES_DISPLAY}
            themeStyles={themeStyles}
            theme={theme}
            onSelectCategory={(category) => {
              setSelectedCategory(category);
              setCurrentPage(1);
              setTimeout(() => {
                productsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            onViewAll={() => {
              setSelectedCategory('all');
              setCurrentPage(1);
              setTimeout(() => {
                productsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          />
        </div>
      )}

      {SHOW_BEST_SELLERS && (
        <FeaturedProductsRow
          products={bestSellers}
          max={BEST_SELLERS_MAX}
          title="Best Sellers"
          subtitle="Customer favorites you do not want to miss"
          badgeText="Best Seller"
          badgeColor={themeStyles?.primary || '#2563eb'}
          surface="alt"
          themeStyles={themeStyles}
          theme={theme}
          formatPrice={formatPrice}
          getStorePath={getStorePath}
          toggleWishlist={toggleWishlist}
          isInWishlist={isInWishlist}
          wishlistLoading={wishlistLoading}
        />
      )}

      {SHOW_TRENDING && (
        <FeaturedProductsRow
          products={trendingProducts}
          max={TRENDING_MAX}
          title="Trending"
          subtitle="What everyone is buying right now"
          badgeText="Trending"
          badgeColor="#8b5cf6"
          surface="base"
          themeStyles={themeStyles}
          theme={theme}
          formatPrice={formatPrice}
          getStorePath={getStorePath}
          toggleWishlist={toggleWishlist}
          isInWishlist={isInWishlist}
          wishlistLoading={wishlistLoading}
        />
      )}

      {SHOW_NEW_ARRIVALS && (
        <FeaturedProductsRow
          products={newArrivals}
          max={NEW_ARRIVALS_MAX}
          title="New Arrivals"
          subtitle="Be the first to discover our latest products"
          badgeText="New"
          badgeColor="#10b981"
          surface="alt"
          themeStyles={themeStyles}
          theme={theme}
          formatPrice={formatPrice}
          getStorePath={getStorePath}
          toggleWishlist={toggleWishlist}
          isInWishlist={isInWishlist}
          wishlistLoading={wishlistLoading}
        />
      )}

      {SHOW_REVIEWS && (
        <ReviewsCarousel storeSlug={store?.slug} themeStyles={themeStyles} theme={theme} />
      )}

      {/* Responsive Grid Styles */}
      <style>{`
        .hp-products-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.25rem;
        }
        .hp-products-grid.list-mode {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }
        @media (max-width: 1024px) {
          .hp-products-grid { grid-template-columns: repeat(3, 1fr); }
          .hp-featured-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 768px) {
          .hp-products-grid { grid-template-columns: repeat(2, 1fr); }
          .hp-featured-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 1rem !important; }
          .hp-section-pad { padding: 2rem 0 !important; }
        }
        @media (max-width: 480px) {
          .hp-products-grid { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
          .hp-featured-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 0.75rem !important; }
        }
      `}</style>

      <HomeProductCatalog
        sectionRef={productsSectionRef}
        selectedCategory={selectedCategory}
        filteredProducts={filteredProducts}
        paginatedProducts={paginatedProducts}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categories={categories}
        onCategoryChange={(cat) => { setSelectedCategory(cat); setCurrentPage(1); }}
        sortBy={sortBy}
        onSortChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        themeStyles={themeStyles}
        theme={theme}
        store={store}
        formatPrice={formatPrice}
        getStorePath={getStorePath}
        dispatch={dispatch}
        transitionCartAction={transitionCartAction}
        isUpdating={isUpdating}
        toggleWishlist={toggleWishlist}
        isInWishlist={isInWishlist}
        wishlistLoading={wishlistLoading}
      />

      {/* WhatsApp floating button moved to StorefrontWhatsAppFloat at
          the layout level — every page now gets it, not just Home. */}
    </div>
  );
};

// Wrapper component with ToastProvider and Theme Support
const HomePage = () => {
  const { store, theme, themeStyles, premiumThemeConfig } = useOutletContext() || {};
  const campaigns = useSelector(state => state.store?.campaigns || []);

  // Hero-flush layout: lifts the floating pill nav out of flow so the
  // hero runs to the viewport top. Lives at the wrapper level so it
  // fires in BOTH render paths — legacy HomePageContent AND the
  // premium-theme ThemedHomePage. Inside HomePageContent it never
  // fires when a premium theme is active, which is the bug we're fixing.
  //
  // Skipped while an announcement bar is actively rendered: settings
  // say enabled + text AND the customer hasn't dismissed it.
  // sf:announcement-dismissed event re-arms hero-flush without a reload.
  const [announcementDismissed, setAnnouncementDismissed] = useState(() => {
    try {
      return window.sessionStorage?.getItem('sf_announcement_dismissed') === 'true';
    } catch { return false; }
  });
  useEffect(() => {
    const handler = () => setAnnouncementDismissed(true);
    window.addEventListener('sf:announcement-dismissed', handler);
    return () => window.removeEventListener('sf:announcement-dismissed', handler);
  }, []);
  const announcementActive = !!(
    store?.settings?.announcement?.enabled &&
    store?.settings?.announcement?.text &&
    !announcementDismissed
  );
  const saleAnnouncementActive = !!campaigns.find(campaign => campaign?.showOnStorefront && campaign?.placement === 'TOP_BAR') && !announcementDismissed;
  useEffect(() => {
    if (announcementActive || saleAnnouncementActive) return undefined;
    document.body.classList.add('sf-hero-flush');
    return () => { document.body.classList.remove('sf-hero-flush'); };
  }, [announcementActive, saleAnnouncementActive]);

  // Use premium theme config from StorefrontLayout context (already fetched)
  // This eliminates duplicate API calls and double preloader
  if (premiumThemeConfig?.themeId) {
    const ThemedHomePage = require('./ThemedHomePage').default;
    return (
      <ToastProvider>
        <ThemedHomePage store={store} themeConfig={premiumThemeConfig} campaigns={campaigns} />
      </ToastProvider>
    );
  }

  // No premium theme - use legacy renderer
  return (
    <ToastProvider>
      <HomePageContent />
    </ToastProvider>
  );
};

export default HomePage;
